/**
 * Profile header section for UserProfileModal.
 * Renders user name, badges, follow/friend/block action buttons, and social links.
 */

import { TierBadge } from '@components/ui/TierBadge';
import {
  buildSocialUrl,
  SOCIAL_PLATFORMS,
  type SocialLinks,
  type SocialPlatformKey,
} from '@parlay-picker/shared/socialLinks';
import {
  BadgeCheck,
  Ban,
  Clock,
  Instagram,
  MessageCircle,
  MoreHorizontal,
  Music2,
  Target,
  Twitch,
  Twitter,
  UserPlus,
  Users,
  Youtube,
} from 'lucide-react';
import type { ReactElement } from 'react';

const SOCIAL_ICON_MAP: Record<string, typeof Twitter> = {
  Twitter,
  Instagram,
  Music2,
  Youtube,
  MessageCircle,
  Twitch,
  Target,
};

interface ProfileHeaderUser {
  id: string;
  displayName: string | null;
  isVerified: boolean;
  createdAt: string | Date;
  followerCount: number;
  followingCount: number;
  linkedSportsbooks?: string[];
  socialLinks?: SocialLinks | null;
}

interface ProfileHeaderCreatorInfo {
  isCreator: boolean;
}

interface ProfileHeaderOverallStats {
  currentStreak: number;
  totalBets: number;
}

export interface ProfileHeaderProps {
  user: ProfileHeaderUser;
  creatorInfo?: ProfileHeaderCreatorInfo | null;
  overallStats: ProfileHeaderOverallStats;
  isOwnProfile: boolean;
  currentUserId?: string;
  isFollowing: boolean;
  followLocked: boolean;
  followLoading: boolean;
  friendLocked: boolean;
  friendshipLoading: boolean;
  friendshipStatus: 'none' | 'pending_sent' | 'pending_received' | 'friends' | null;
  blockLoading: boolean;
  showBlockMenu: boolean;
  optimisticFollowerCount: number | null;
  friendCount: number;
  onFollowToggle: () => void;
  onShowFollowUpgrade: () => void;
  onSendFriendRequest: () => void;
  onCancelFriendRequest: () => void;
  onAcceptFriendRequest: () => void;
  onRejectFriendRequest: () => void;
  onUnfriend: () => void;
  onBlockUser: () => void;
  onToggleBlockMenu: () => void;
}

export function ProfileHeader({
  user,
  creatorInfo,
  overallStats,
  isOwnProfile,
  currentUserId,
  isFollowing,
  followLocked,
  followLoading,
  friendLocked,
  friendshipLoading,
  friendshipStatus,
  blockLoading,
  showBlockMenu,
  optimisticFollowerCount,
  friendCount,
  onFollowToggle,
  onShowFollowUpgrade,
  onSendFriendRequest,
  onCancelFriendRequest,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  onUnfriend,
  onBlockUser,
  onToggleBlockMenu,
}: ProfileHeaderProps): ReactElement {
  const displayFollowerCount = optimisticFollowerCount ?? user.followerCount;
  const socialLinks = user.socialLinks;
  const activePlatforms = socialLinks
    ? SOCIAL_PLATFORMS.filter((p) => socialLinks[p.key as SocialPlatformKey])
    : [];

  return (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-100">{user.displayName || 'Anonymous'}</h3>

          {/* Action buttons — only when viewing another user's profile */}
          {currentUserId && !isOwnProfile && (
            <div className="flex items-center gap-2">
              {/* Follow button */}
              {followLocked ? (
                <button
                  type="button"
                  onClick={onShowFollowUpgrade}
                  className="px-4 py-2 rounded-lg font-medium transition-colors bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover flex items-center gap-2"
                >
                  Follow
                  <TierBadge feature="social_follow" size="md" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onFollowToggle}
                  disabled={followLoading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isFollowing
                      ? 'bg-theme-surface-hover text-gray-200 hover:bg-theme-surface-hover'
                      : 'bg-theme-primary-fill text-theme-on-primary-fill hover:bg-theme-primary-fill'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {followLoading ? 'Loading...' : isFollowing ? 'Following' : 'Follow'}
                </button>
              )}

              {/* Friend button */}
              {friendshipStatus === 'none' &&
                (friendLocked ? (
                  <button
                    type="button"
                    data-testid="friend-button"
                    data-friendship-status="locked"
                    onClick={onShowFollowUpgrade}
                    className="px-3 py-2 rounded-lg font-medium transition-colors bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover flex items-center gap-1.5 text-sm"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Friend
                    <TierBadge feature="social_friend" size="md" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="friend-button"
                    data-friendship-status="none"
                    onClick={onSendFriendRequest}
                    disabled={friendshipLoading}
                    className="px-3 py-2 rounded-lg font-medium transition-colors bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-primary-fill hover:text-theme-on-primary-fill flex items-center gap-1.5 text-sm disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Friend
                  </button>
                ))}

              {friendshipStatus === 'pending_sent' && (
                <button
                  type="button"
                  data-testid="friend-button"
                  data-friendship-status="pending_sent"
                  onClick={onCancelFriendRequest}
                  disabled={friendshipLoading}
                  className="px-3 py-2 rounded-lg font-medium transition-colors bg-theme-surface-hover text-theme-text-tertiary flex items-center gap-1.5 text-sm"
                >
                  <Clock className="w-4 h-4" />
                  Pending
                </button>
              )}

              {friendshipStatus === 'pending_received' && (
                <div className="flex items-center gap-1" data-testid="friend-pending-received">
                  <button
                    type="button"
                    data-testid="friend-accept-button"
                    onClick={onAcceptFriendRequest}
                    disabled={friendshipLoading}
                    className="px-3 py-2 rounded-lg font-medium transition-colors bg-theme-success text-white hover:bg-theme-success/80 text-sm disabled:opacity-50"
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    data-testid="friend-decline-button"
                    onClick={onRejectFriendRequest}
                    disabled={friendshipLoading}
                    className="px-3 py-2 rounded-lg font-medium transition-colors bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-danger/20 hover:text-theme-danger text-sm disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              )}

              {friendshipStatus === 'friends' && (
                <button
                  type="button"
                  data-testid="friend-button"
                  data-friendship-status="friends"
                  onClick={onUnfriend}
                  disabled={friendshipLoading}
                  className="px-3 py-2 rounded-lg font-medium transition-colors bg-theme-surface-hover text-theme-success flex items-center gap-1.5 text-sm hover:bg-theme-danger/20 hover:text-theme-danger"
                >
                  <Users className="w-4 h-4" />
                  Friends
                </button>
              )}

              {/* More menu (block only: a blocked profile never loads, so unblocking is in Settings) */}
              <div className="relative">
                <button
                  type="button"
                  data-testid="profile-more-menu-button"
                  data-open={showBlockMenu ? 'true' : 'false'}
                  onClick={onToggleBlockMenu}
                  className="p-2 rounded-lg text-theme-text-tertiary hover:bg-theme-surface-hover transition-colors"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                {showBlockMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-theme-surface border border-theme-border rounded-lg shadow-xl z-50 py-1">
                    <button
                      type="button"
                      onClick={onBlockUser}
                      disabled={blockLoading}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-danger hover:bg-theme-danger/10"
                    >
                      <Ban className="w-4 h-4" />
                      Block User
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Badges row */}
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex items-center gap-2 flex-wrap">
            {creatorInfo?.isCreator && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-theme-primary/15 rounded-md border border-theme-primary/30">
                <BadgeCheck className="w-4 h-4 text-theme-primary" />
                <span className="text-xs text-theme-primary font-medium">Creator</span>
              </div>
            )}
            {user.isVerified && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-theme-primary/10 rounded-md border border-theme-primary/30">
                <BadgeCheck className="w-4 h-4 text-theme-primary" />
                <span className="text-xs text-theme-primary font-medium">Verified</span>
              </div>
            )}
            {overallStats.currentStreak >= 5 && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-orange-500/10 rounded-md border border-orange-500/30">
                <span className="text-sm">🔥</span>
                <span className="text-xs text-orange-400 font-medium">Hot Streak</span>
              </div>
            )}
            {overallStats.totalBets >= 100 && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-purple-500/10 rounded-md border border-purple-500/30">
                <span className="text-sm">💯</span>
                <span className="text-xs text-purple-400 font-medium">100 Bet Club</span>
              </div>
            )}
          </div>

          {/* Member since + follower counts */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-theme-text-secondary">
              Member since{' '}
              {new Date(user.createdAt).toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-theme-text-secondary">
                <span className="font-semibold text-gray-200">{displayFollowerCount}</span>{' '}
                {displayFollowerCount === 1 ? 'follower' : 'followers'}
              </span>
              <span className="text-theme-text-secondary">
                <span className="font-semibold text-gray-200">{user.followingCount}</span> following
              </span>
              {isOwnProfile && (
                <span className="text-theme-text-secondary">
                  <span className="font-semibold text-gray-200">{friendCount || 0}</span>{' '}
                  {friendCount === 1 ? 'friend' : 'friends'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Linked sportsbooks — verified users only */}
        {user.isVerified && user.linkedSportsbooks && user.linkedSportsbooks.length > 0 && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-theme-text-tertiary">Linked:</span>
            {user.linkedSportsbooks.map((book, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 bg-theme-surface-hover/50 rounded text-theme-text-secondary"
              >
                {book}
              </span>
            ))}
          </div>
        )}

        {/* Social links */}
        {activePlatforms.length > 0 && (
          <div className="flex items-center gap-2 mt-3 mb-1">
            {activePlatforms.map((platform) => {
              const Icon = SOCIAL_ICON_MAP[platform.icon];
              const handle = socialLinks![platform.key as SocialPlatformKey]!;
              const url = buildSocialUrl(platform.key as SocialPlatformKey, handle);
              const inner = (
                <div
                  key={platform.key}
                  className="p-2 rounded-lg bg-theme-surface-hover/50 hover:bg-theme-surface-hover transition-colors cursor-pointer"
                  title={`${platform.label}: ${handle}`}
                >
                  {Icon && <Icon className="w-4 h-4 text-theme-text-secondary" />}
                </div>
              );
              return url ? (
                <a key={platform.key} href={url} target="_blank" rel="noopener noreferrer">
                  {inner}
                </a>
              ) : (
                <div key={platform.key}>{inner}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
